// Footstep / impact surface lookup: map brush -> audio surface, from the map file materials.

import type { FootstepSurface } from "../audio";
import { SurfaceFlags } from "../wasm/types";
import type { RayHitData } from "../wasm/types";

/**
 * The subset of the map file this module reads. Brushes are `unknown` in render/mapTypes.ts
 * (only the Rust side parses them), so `mat` is read defensively.
 */
export interface SurfaceMapSource {
  materials: Record<string, { texture: string }>;
  brushes: readonly unknown[];
}

const TEXTURE_SURFACE: Readonly<Record<string, FootstepSurface>> = {
  metal: "metal",
  grate: "metal",
  panel: "metal",
  stripes: "metal",
  glass: "glass",
  wood: "wood",
};

export function surfaceForTexture(texture: string): FootstepSurface {
  return TEXTURE_SURFACE[texture] ?? "concrete";
}

/** Surface per brush index, built once per map. */
export class SurfaceTable {
  private surfaces: FootstepSurface[] = [];

  load(map: SurfaceMapSource): void {
    this.surfaces = map.brushes.map((b) => {
      const mat = (b as { mat?: unknown } | null)?.mat;
      const texture = typeof mat === "string" ? map.materials[mat]?.texture : undefined;
      return surfaceForTexture(texture ?? "concrete");
    });
  }

  clear(): void {
    this.surfaces = [];
  }

  /** Surface of a raycast hit; the glass flag wins over the material. */
  of(hit: RayHitData): FootstepSurface {
    if ((hit.surface & SurfaceFlags.GLASS) !== 0) return "glass";
    return this.surfaces[hit.brush] ?? "concrete";
  }
}
